// WARNING: This multi-select implementation is not ideal from a UI or
// accessibility perspective, but it is good enough to ship. Ideally this would
// be implemented as an ARIA listbox, but we're just going with a combination of
// existing Zinnia components for now in order to speed up development. If/when
// we improve this, we should see if we can integrate our new component upstream
// into Zinnia, since other teams are solving similar problems.
//
// Sage Fennel (2023-02-06)
// #team-partner-sharing

/** @jsx jsx */
import { css, jsx, SerializedStyles } from '@emotion/core';
import { Icon } from '@zapier/zinnia-icons';
import {
  Checkbox,
  CheckboxGroup,
  Colors,
  Popover,
  Tooltip,
  TooltipFocusableTrigger,
  Typography,
} from '@zapier/design-system';
import { Button as RACButton, DialogTrigger } from 'react-aria-components';
import { Fragment, ReactNode, useRef, useState } from 'react';

const styleContainer = css`
  display: flex;
  flex-direction: column;
  user-select: none;
  width: 100%;
  min-width: 0;
`;

const styleButton = css`
  all: unset;
  box-sizing: border-box;
  ${Typography.Body2}
  background: ${Colors.PrimeWhite};
  border: 1px solid ${Colors.GrayWarm6};
  border-radius: 3px;
  padding-left: 15px;
  padding-right: 5px;
  height: 42px;
  text-align: left;
  /* OK, so interactive elements shouldn't get "cursor: pointer" unless they're
  "<a>" tags or something simulating on "<a>" tag (which is usually a bad idea)
  BUT everything in Zinnia uses the "pointer" (👆) cursor, so I'm copying it
  here out of consistency. */
  cursor: pointer;

  &:hover, &:focus {
    border-color: ${Colors.UiPrimaryStrongest};
  }

  &:focus {
    outline: 2px solid transparent;
    box-shadow: 0 0 0 1px ${Colors.UiPrimaryStrongest};
  }

  & > div {
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 10px;
  }

  & > div > div:first-of-type {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: auto;
  }
`;

const styleMenuItemsContainer = css`
  padding: 10px;
`;

type MultiSelectItem = {
  value: string;
  label: string;
};

type Placement =
  | 'bottom'
  | 'top'
  | 'bottom start'
  | 'bottom end'
  | 'top start'
  | 'top end';

type Props = {
  children: ReactNode;
  items: MultiSelectItem[];
  selectedItems: MultiSelectItem[];
  onChange: (selectedItems: MultiSelectItem[]) => void;
  position?: 'south' | 'north';
  ariaLabel?: string;
  maxSelectedItems?: number;
  extraStyleMenuItemsContainer?: SerializedStyles;
  disabledItemTooltip?: string;
  emptyMessage?: string;
};

export default function MultiSelect({
  ariaLabel,
  children,
  items,
  onChange,
  position = 'south',
  selectedItems,
  maxSelectedItems,
  extraStyleMenuItemsContainer,
  disabledItemTooltip,
  emptyMessage,
}: Props) {
  const buttonRef = useRef<HTMLButtonElement>();
  const [isOpen, setIsOpen] = useState(false);

  const selectedItemsValues = new Set(selectedItems.map(item => item.value));

  const placement: Placement = position === 'north' ? 'top' : 'bottom';

  return (
    <div css={styleContainer}>
      <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        <RACButton
          aria-label={ariaLabel}
          css={styleButton}
          excludeFromTabOrder={false}
          ref={buttonRef}
        >
          <div>
            <div>{children}</div>
            <Icon name="arrowDropdown" size={24} />
          </div>
        </RACButton>
        <Popover
          aria-label="Select options"
          color="light"
          isCloseHidden={true}
          placement={placement}
        >
          <div css={[styleMenuItemsContainer, extraStyleMenuItemsContainer]}>
            {items?.length ? (
              <CheckboxGroup
                aria-label="Select options"
                onChange={(newValues: string[]) => {
                  onChange(items.filter(x => newValues.includes(x.value)));
                }}
                value={Array.from(selectedItemsValues)}
              >
                {items.map((item, index) => {
                  const checked = selectedItemsValues.has(item.value);
                  const isDisabled = Boolean(
                    selectedItemsValues.size === maxSelectedItems && !checked
                  );

                  const checkbox = (
                    <Checkbox isDisabled={isDisabled} value={item.value}>
                      {item.label}
                    </Checkbox>
                  );

                  return isDisabled && disabledItemTooltip ? (
                    <Tooltip
                      content={disabledItemTooltip}
                      key={item.value}
                      placement={
                        items.length - 1 === index ? 'top left' : 'bottom left'
                      }
                    >
                      <TooltipFocusableTrigger tag="div">
                        {checkbox}
                      </TooltipFocusableTrigger>
                    </Tooltip>
                  ) : (
                    <Fragment key={item.value}>{checkbox}</Fragment>
                  );
                })}
              </CheckboxGroup>
            ) : (
              emptyMessage && <span>{emptyMessage}</span>
            )}
          </div>
        </Popover>
      </DialogTrigger>
    </div>
  );
}
